import React, { useEffect, useMemo, useRef } from "react";

/**
 * Voxara's android scribe.
 *
 * Every motion is driven by `level` (0..1), which the recorder feeds from a real AnalyserNode.
 * That is deliberate: while recording we show no transcript, so the mascot IS the signal that
 * audio is still arriving. A still mascot means silence, and the user must be able to read
 * that at a glance from across the room.
 */
export default function VoxaraScribe({ levelRef, active }) {
  const bobRef = useRef(null);
  const headRef = useRef(null);
  const orbRef = useRef(null);
  const coreRef = useRef(null);
  const eyeLRef = useRef(null);
  const eyeRRef = useRef(null);
  const penRef = useRef(null);
  const armLRef = useRef(null);
  const armRRef = useRef(null);
  const handRRef = useRef(null);
  const inkRef = useRef(null);
  const rulesRef = useRef(null);
  const marginRef = useRef(null);
  const waveRefs = useRef([]);
  const rafRef = useRef(null);
  const stateRef = useRef({ row: 0, drawn: 0, lvl: 0, t: 0, blinkAt: 0 });

  // Sheet corners: the ruled lines and the handwriting are mapped through these, so the page
  // and everything written on it share one perspective.
  const geom = useMemo(() => {
    const TL = { x: 186, y: 168 };
    const TR = { x: 258, y: 168 };
    const BL = { x: 156, y: 228 };
    const BR = { x: 288, y: 228 };
    const lerp = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    const P = (u, v) => lerp(lerp(TL, TR, u), lerp(BL, BR, u), v);
    return { P, rows: [0.22, 0.4, 0.58, 0.76, 0.92], ends: [0.85, 0.7, 0.88, 0.64, 0.8] };
  }, []);

  useEffect(() => {
    const NS = "http://www.w3.org/2000/svg";
    const { P, rows, ends } = geom;

    // ruled lines + red margin
    if (rulesRef.current) {
      rulesRef.current.innerHTML = "";
      rows.forEach((v) => {
        const a = P(0.12, v);
        const b = P(0.92, v);
        const l = document.createElementNS(NS, "line");
        l.setAttribute("x1", a.x.toFixed(1));
        l.setAttribute("y1", a.y.toFixed(1));
        l.setAttribute("x2", b.x.toFixed(1));
        l.setAttribute("y2", b.y.toFixed(1));
        rulesRef.current.appendChild(l);
      });
    }
    if (marginRef.current) {
      const m0 = P(0.18, 0.06);
      const m1 = P(0.18, 0.97);
      marginRef.current.setAttribute("d", `M${m0.x.toFixed(1)} ${m0.y.toFixed(1)} L${m1.x.toFixed(1)} ${m1.y.toFixed(1)}`);
    }

    // handwriting rows, revealed progressively
    const paths = [];
    if (inkRef.current) {
      inkRef.current.innerHTML = "";
      rows.forEach((v, i) => {
        let d = "";
        let up = true;
        const steps = 22;
        for (let s = 0; s <= steps; s += 1) {
          const u = 0.22 + (ends[i] - 0.22) * (s / steps);
          const amp = (up ? -1 : 1) * 2.2 * (0.8 + v * 0.5);
          const pt = P(u, v);
          d += `${s === 0 ? "M" : "L"}${pt.x.toFixed(1)} ${(pt.y + amp).toFixed(1)}`;
          up = !up;
        }
        const p = document.createElementNS(NS, "path");
        p.setAttribute("d", d);
        inkRef.current.appendChild(p);
        const len = p.getTotalLength();
        p.setAttribute("stroke-dasharray", len);
        p.setAttribute("stroke-dashoffset", len);
        paths.push({ el: p, len });
      });
    }

    const SHL = { x: 196, y: 120 };
    const SHR = { x: 244, y: 120 };
    const HL = { x: 170, y: 200 };
    const limb = (s, h, bend) => {
      const mx = (s.x + h.x) / 2 + bend;
      const my = (s.y + h.y) / 2 + 6;
      return `M${s.x} ${s.y} Q${mx.toFixed(1)} ${my.toFixed(1)} ${h.x.toFixed(1)} ${h.y.toFixed(1)}`;
    };

    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const newPage = () => {
      paths.forEach((p) => {
        p.el.style.transition = "opacity .5s ease";
        p.el.style.opacity = "0";
      });
      window.setTimeout(() => {
        paths.forEach((p) => {
          p.el.setAttribute("stroke-dashoffset", p.len);
          p.el.style.transition = "none";
          p.el.style.opacity = "1";
        });
        stateRef.current.row = 0;
        stateRef.current.drawn = 0;
      }, 520);
    };

    const frame = (ts) => {
      const st = stateRef.current;
      st.t += 1;
      const target = active ? Math.max(0, Math.min(1, levelRef.current || 0)) : 0;
      st.lvl += (target - st.lvl) * 0.18;
      const lvl = st.lvl;

      if (bobRef.current) bobRef.current.setAttribute("transform", `translate(0 ${(-lvl * 2).toFixed(2)})`);
      if (headRef.current) headRef.current.setAttribute("transform", `rotate(${active ? 4 : 0} 220 80)`);
      if (orbRef.current) orbRef.current.setAttribute("r", (4.6 + lvl * 2.6).toFixed(2));
      if (coreRef.current) coreRef.current.setAttribute("r", (4.4 + lvl * 1.8).toFixed(2));

      waveRefs.current.forEach((wv, i) => {
        if (!wv) return;
        const th = Math.max(0, lvl - (i % 2) * 0.26);
        wv.style.opacity = (th * (0.8 - (i % 2) * 0.22)).toFixed(2);
        wv.setAttribute("stroke-width", (1.8 + th * 1.8).toFixed(2));
      });

      if (active && st.row < paths.length) {
        const cur = paths[st.row];
        st.drawn += 0.5 + lvl * 2.6;
        if (st.drawn >= cur.len) {
          cur.el.setAttribute("stroke-dashoffset", 0);
          st.drawn = 0;
          st.row += 1;
          if (st.row >= paths.length) newPage();
        } else {
          cur.el.setAttribute("stroke-dashoffset", (cur.len - st.drawn).toFixed(1));
        }
      }

      if (paths.length) {
        const act = paths[Math.min(st.row, paths.length - 1)];
        const pt = act.el.getPointAtLength(Math.min(st.drawn, act.len));
        const lift = active ? 0 : -10;
        const HR = { x: pt.x + 7, y: pt.y + lift - 5 };
        if (penRef.current) penRef.current.setAttribute("transform", `translate(${pt.x.toFixed(1)} ${(pt.y + lift).toFixed(1)}) rotate(20)`);
        if (handRRef.current) {
          handRRef.current.setAttribute("cx", HR.x.toFixed(1));
          handRRef.current.setAttribute("cy", HR.y.toFixed(1));
        }
        if (armRRef.current) armRRef.current.setAttribute("d", limb(SHR, HR, 18));
        if (armLRef.current) armLRef.current.setAttribute("d", limb(SHL, HL, -16));
      }

      if (ts > st.blinkAt) {
        st.blinkAt = ts + 2400 + Math.random() * 2400;
        [eyeLRef.current, eyeRRef.current].forEach((e) => {
          if (!e) return;
          e.setAttribute("height", "3");
          e.setAttribute("y", "72");
          window.setTimeout(() => {
            e.setAttribute("height", "12");
            e.setAttribute("y", "66");
          }, 130);
        });
      }

      rafRef.current = requestAnimationFrame(frame);
    };

    if (reduce) {
      paths.forEach((p) => p.el.setAttribute("stroke-dashoffset", 0));
      if (armLRef.current) armLRef.current.setAttribute("d", limb(SHL, HL, -16));
      if (armRRef.current) armRRef.current.setAttribute("d", limb(SHR, { x: 250, y: 196 }, 18));
    } else {
      rafRef.current = requestAnimationFrame(frame);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [geom, levelRef, active]);

  const setWave = (i) => (el) => {
    waveRefs.current[i] = el;
  };

  return (
    <svg viewBox="0 0 440 260" className="w-full max-w-[500px] h-auto block" role="img"
         aria-label="Mascota de Voxara escuchando y transcribiendo">
      <defs>
        <linearGradient id="vsBody" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#22d3ee" /><stop offset="55%" stopColor="#a855f7" /><stop offset="100%" stopColor="#f800f8" />
        </linearGradient>
        <linearGradient id="vsDesk" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#26262c" /><stop offset="100%" stopColor="#151519" />
        </linearGradient>
        <radialGradient id="vsOrb">
          <stop offset="0%" stopColor="#fff" /><stop offset="50%" stopColor="#fbbf24" /><stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
      </defs>

      <g fill="none" stroke="#22d3ee" strokeLinecap="round" strokeWidth="2.5">
        <path ref={setWave(0)} d="M156 50 a16 16 0 0 0 0 30" opacity="0" />
        <path ref={setWave(1)} d="M146 40 a26 26 0 0 0 0 50" opacity="0" />
        <path ref={setWave(2)} d="M284 50 a16 16 0 0 1 0 30" opacity="0" />
        <path ref={setWave(3)} d="M294 40 a26 26 0 0 1 0 50" opacity="0" />
      </g>

      <g ref={bobRef}>
        <g ref={headRef}>
          <line x1="220" y1="34" x2="220" y2="18" stroke="#a855f7" strokeWidth="2.5" strokeLinecap="round" />
          <circle ref={orbRef} cx="220" cy="14" r="5" fill="url(#vsOrb)" />
          <rect x="178" y="32" width="84" height="72" rx="26" fill="#17171c" stroke="url(#vsBody)" strokeWidth="2.5" />
          <rect x="190" y="48" width="60" height="46" rx="17" fill="#0a0a0e" />
          <rect ref={eyeLRef} x="204" y="66" width="9" height="12" rx="4.5" fill="#22d3ee" />
          <rect ref={eyeRRef} x="227" y="66" width="9" height="12" rx="4.5" fill="#22d3ee" />
          <path d="M212 84 q8 5 16 0" fill="none" stroke="#22d3ee" strokeWidth="2.2" strokeLinecap="round" opacity=".85" />
          <path d="M174 60 q-2 -32 46 -32 q48 0 46 32" fill="none" stroke="#a855f7" strokeWidth="4.5" strokeLinecap="round" />
          <rect x="166" y="54" width="14" height="28" rx="7" fill="#c026d3" />
          <rect x="260" y="54" width="14" height="28" rx="7" fill="#c026d3" />
        </g>
        <rect x="192" y="102" width="56" height="56" rx="20" fill="#17171c" stroke="url(#vsBody)" strokeWidth="2.5" />
        <circle ref={coreRef} cx="220" cy="124" r="5" fill="#f800f8" />
      </g>

      <path d="M34 254 L96 156 L344 156 L406 254 Z" fill="url(#vsDesk)" />
      <line x1="96" y1="156" x2="344" y2="156" stroke="#33333a" strokeWidth="2" />

      <path d="M186 168 L258 168 L288 228 L156 228 Z" fill="#f6f4ef" stroke="#d8d4ca" strokeWidth="1.5" strokeLinejoin="round" />
      <g ref={rulesRef} stroke="#e2ded4" strokeWidth="1" />
      <path ref={marginRef} d="" stroke="#f0b0b0" strokeWidth="1" fill="none" />
      <g ref={inkRef} fill="none" stroke="#2b2b33" strokeWidth="1.9" strokeLinecap="round" />

      <path ref={armLRef} d="" fill="none" stroke="#7c3aed" strokeWidth="7.5" strokeLinecap="round" />
      <path ref={armRRef} d="" fill="none" stroke="#7c3aed" strokeWidth="7.5" strokeLinecap="round" />
      <circle cx="196" cy="120" r="5.5" fill="#8b5cf6" />
      <circle cx="244" cy="120" r="5.5" fill="#8b5cf6" />
      <circle cx="170" cy="200" r="7" fill="#a855f7" />
      <circle ref={handRRef} cx="250" cy="196" r="7" fill="#a855f7" />

      <g ref={penRef}>
        <rect x="-2.5" y="-40" width="5" height="34" rx="2" fill="#fbbf24" />
        <rect x="-2.5" y="-45" width="5" height="6" rx="1.5" fill="#f59e0b" />
        <path d="M-2.5 -6 l2.5 6 l2.5 -6 z" fill="#4b4b55" />
      </g>
    </svg>
  );
}
